import React, { useCallback } from 'react';
import { Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { useAnimationStore } from '../../store/animationStore';
import { cn } from '../../utils/cn';

interface OnionSkinningProps {
  className?: string;
}

export const OnionSkinning: React.FC<OnionSkinningProps> = ({ className }) => {
  const { onionSkin, setOnionSkin, toggleOnionSkin } = useAnimationStore();

  // Handle toggle
  const handleToggle = useCallback(() => {
    toggleOnionSkin();
  }, [toggleOnionSkin]);

  // Handle previous frames settings
  const handleShowPreviousChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setOnionSkin({ showPrevious: e.target.checked });
    },
    [setOnionSkin]
  );

  const handlePreviousCountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value, 10);
      if (!isNaN(value)) {
        setOnionSkin({ previousCount: Math.max(1, Math.min(10, value)) });
      }
    },
    [setOnionSkin]
  );

  const handlePreviousOpacityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      if (!isNaN(value)) {
        setOnionSkin({ previousOpacity: Math.max(0, Math.min(1, value)) });
      }
    },
    [setOnionSkin]
  );

  const handlePreviousTintChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setOnionSkin({ previousTint: e.target.value });
    },
    [setOnionSkin]
  );

  // Handle next frames settings
  const handleShowNextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setOnionSkin({ showNext: e.target.checked });
    },
    [setOnionSkin]
  );

  const handleNextCountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value, 10);
      if (!isNaN(value)) {
        setOnionSkin({ nextCount: Math.max(1, Math.min(10, value)) });
      }
    },
    [setOnionSkin]
  );

  const handleNextOpacityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      if (!isNaN(value)) {
        setOnionSkin({ nextOpacity: Math.max(0, Math.min(1, value)) });
      }
    },
    [setOnionSkin]
  );

  const handleNextTintChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setOnionSkin({ nextTint: e.target.value });
    },
    [setOnionSkin]
  );

  return (
    <div className={cn('bg-zinc-900 border-b border-zinc-700', className)}>
      {/* Header / Toggle */}
      <button
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left',
          'hover:bg-zinc-800/50 transition-colors'
        )}
        onClick={handleToggle}
      >
        {onionSkin.enabled ? (
          <ChevronDown size={14} className="text-zinc-400" />
        ) : (
          <ChevronRight size={14} className="text-zinc-400" />
        )}
        <Layers
          size={14}
          className={onionSkin.enabled ? 'text-blue-400' : 'text-zinc-400'}
        />
        <span
          className={cn(
            'text-sm font-medium',
            onionSkin.enabled ? 'text-zinc-200' : 'text-zinc-400'
          )}
        >
          Onion Skinning
        </span>
        {onionSkin.enabled && (
          <span className="ml-auto text-xs text-blue-400">ON</span>
        )}
      </button>

      {/* Settings panel (collapsed when disabled) */}
      {onionSkin.enabled && (
        <div className="px-3 pb-3 space-y-4">
          {/* Previous Frames */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={onionSkin.showPrevious}
                onChange={handleShowPreviousChange}
                className="w-3.5 h-3.5 rounded border-zinc-600 bg-zinc-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
              />
              <span className="text-xs font-medium text-zinc-300">
                Previous Frames
              </span>
            </label>

            {onionSkin.showPrevious && (
              <div className="pl-5 space-y-2">
                {/* Count */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500 w-16">Count:</label>
                  <input
                    type="number"
                    value={onionSkin.previousCount}
                    onChange={handlePreviousCountChange}
                    min={1}
                    max={10}
                    className={cn(
                      'w-16 bg-zinc-800 text-zinc-200 text-xs px-2 py-1 rounded',
                      'border border-zinc-700 focus:border-blue-500 focus:outline-none'
                    )}
                  />
                </div>

                {/* Opacity */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500 w-16">Opacity:</label>
                  <input
                    type="range"
                    value={onionSkin.previousOpacity}
                    onChange={handlePreviousOpacityChange}
                    min={0}
                    max={1}
                    step={0.05}
                    className="flex-1 h-1 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className="text-xs text-zinc-400 w-8 text-right">
                    {Math.round(onionSkin.previousOpacity * 100)}%
                  </span>
                </div>

                {/* Color */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500 w-16">Color:</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={onionSkin.previousTint}
                      onChange={handlePreviousTintChange}
                      className="w-6 h-6 rounded border border-zinc-600 cursor-pointer bg-transparent"
                    />
                    <span className="text-xs text-zinc-400 font-mono">
                      {onionSkin.previousTint}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Separator */}
          <div className="border-t border-zinc-800" />

          {/* Next Frames */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={onionSkin.showNext}
                onChange={handleShowNextChange}
                className="w-3.5 h-3.5 rounded border-zinc-600 bg-zinc-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
              />
              <span className="text-xs font-medium text-zinc-300">
                Next Frames
              </span>
            </label>

            {onionSkin.showNext && (
              <div className="pl-5 space-y-2">
                {/* Count */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500 w-16">Count:</label>
                  <input
                    type="number"
                    value={onionSkin.nextCount}
                    onChange={handleNextCountChange}
                    min={1}
                    max={10}
                    className={cn(
                      'w-16 bg-zinc-800 text-zinc-200 text-xs px-2 py-1 rounded',
                      'border border-zinc-700 focus:border-blue-500 focus:outline-none'
                    )}
                  />
                </div>

                {/* Opacity */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500 w-16">Opacity:</label>
                  <input
                    type="range"
                    value={onionSkin.nextOpacity}
                    onChange={handleNextOpacityChange}
                    min={0}
                    max={1}
                    step={0.05}
                    className="flex-1 h-1 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className="text-xs text-zinc-400 w-8 text-right">
                    {Math.round(onionSkin.nextOpacity * 100)}%
                  </span>
                </div>

                {/* Color */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500 w-16">Color:</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={onionSkin.nextTint}
                      onChange={handleNextTintChange}
                      className="w-6 h-6 rounded border border-zinc-600 cursor-pointer bg-transparent"
                    />
                    <span className="text-xs text-zinc-400 font-mono">
                      {onionSkin.nextTint}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnionSkinning;
